import { useTranslation } from "react-i18next";
import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";

import AutocompleteIcon from "../../assets/icons/MUI/ComponentsIcons";
import { entityGenderEnum } from "../../enums/entity.enum";
import { entityGender, entityRoles } from "../../constants/prefilled.constants";
import { colors } from "../../theme/palette";
import { useCountryListing } from "../../api/queries/network/useCountryListing";

const SearchFilter = ({
  isLoading,
  setIsLoading,
  gender,
  setGender,
  nationality,
  setNationality,
  country,
  setCountry,
  persona,
  setPersona,
  type,
  resetFilters,
  isVerified,
  setIsVerified,
}: {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  gender: {
    label: string;
    value: entityGenderEnum;
  } | null;
  setGender: React.Dispatch<
    React.SetStateAction<{
      label: string;
      value: entityGenderEnum;
    } | null>
  >;
  nationality: {
    label: string;
    value: string;
  } | null;
  setNationality: React.Dispatch<
    React.SetStateAction<{
      label: string;
      value: string;
    } | null>
  >;
  country: {
    label: string;
    value: string;
  } | null;
  setCountry: React.Dispatch<
    React.SetStateAction<{
      label: string;
      value: string;
    } | null>
  >;
  persona: {
    label: string;
    value: string;
  } | null;
  setPersona: React.Dispatch<
    React.SetStateAction<{
      label: string;
      value: string;
    } | null>
  >;
  isVerified: {
    label: string;
    value: string;
  } | null;
  setIsVerified: React.Dispatch<
    React.SetStateAction<{
      label: string;
      value: string;
    } | null>
  >;
  type?: string;
  resetFilters: () => void;
}) => {
  const { t } = useTranslation();

  const { countries, nationalities } = useCountryListing();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        rowGap: "22px",
        width: "100%",
        marginBottom: "22px",
      }}
    >
      <Typography variant="h4ExtraBold">{t("pages.search.search")}</Typography>
      <Typography
        sx={{
          display: "block",
          color: colors.Black100,
          fontFamily: "Inter",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "normal",
        }}
      >
        {type === "entities" ? t("pages.search.filterFootballEntities") : t("pages.search.filterTalents")}
      </Typography>
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          whiteSpace: "nowrap",
          padding: "0 8px 8px 0",
        }}
      >
        <Box
          sx={{
            height: "40px",
            width: "fit-content",
            display: "flex",
            gap: "10px",
          }}
        >
          {type === "users" && (
            <Autocomplete
              options={
                entityRoles.map((el) => ({
                  ...el,
                  label: t(`enums.persona.role.${el.label.toLocaleUpperCase()}`),
                })) || []
              }
              disabled={isLoading}
              value={persona}
              onChange={(event, option) => {
                setPersona(option || null);
              }}
              sx={{
                width: "140px",
              }}
              popupIcon={<AutocompleteIcon />}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: "14px" },
                  }}
                  placeholder={t("pages.goStar.role")}
                />
              )}
            />
          )}

          {type === "entities" && (
            <Autocomplete
              options={
                ["verified", "unverified"].map((el) => ({
                  value: el.toUpperCase(),
                  label: t(`pages.search.${el}`),
                })) || []
              }
              disabled={isLoading}
              value={isVerified}
              onChange={(event, option) => {
                setIsVerified(option || null);
              }}
              sx={{
                width: "140px",
              }}
              popupIcon={<AutocompleteIcon />}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: "14px" },
                  }}
                  placeholder={t("pages.search.verification")}
                />
              )}
            />
          )}

          <Autocomplete
            options={countries || []}
            disabled={isLoading}
            value={country}
            onChange={(event, option) => {
              setCountry(option || null);
            }}
            sx={{
              width: "140px",
            }}
            popupIcon={<AutocompleteIcon />}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  style: { fontSize: "14px" },
                }}
                placeholder={t("pages.goStar.country")}
              />
            )}
          />

          {type === "users" && (
            <Autocomplete
              options={nationalities || []}
              disabled={isLoading}
              value={nationality}
              onChange={(event, option) => {
                setNationality(option || null);
              }}
              sx={{
                width: "140px",
              }}
              popupIcon={<AutocompleteIcon />}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: "14px" },
                  }}
                  placeholder={t("pages.goStar.nationality")}
                />
              )}
            />
          )}

          {type === "users" && (
            <Autocomplete
              options={
                entityGender.map((el) => ({
                  ...el,
                  label: t(`constant.gender.${el.label}`),
                })) || []
              }
              disabled={isLoading}
              value={gender}
              onChange={(event, option) => {
                setGender(option || null);
              }}
              sx={{
                width: "125px",
              }}
              popupIcon={<AutocompleteIcon />}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: "14px" },
                  }}
                  placeholder={t("pages.goStar.gender")}
                />
              )}
            />
          )}
          <Button onClick={resetFilters} disabled={!gender && !persona && !nationality && !country && !isVerified}>
            {t("pages.search.reset")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SearchFilter;
